import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { doc, onSnapshot } from 'firebase/firestore';
import { Firestore } from '../FirebaseAuth/firebase';
import { format, parseISO } from 'date-fns';

const AggregatedTrafficChart = ({ 
  level, // 'account' or 'organization'
  levelId, // accountId or organizationId
  title = "Traffic Overview",
  compact = false // Whether to show compact view
}) => {
  const [aggregatedData, setAggregatedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState('trend'); // 'trend' or 'distribution'
  const [timeRange, setTimeRange] = useState('6months');

  useEffect(() => {
    if (!levelId || !level) return;

    const docPath = level === 'account' 
      ? `accounts/${levelId}/trafficAggregation/summary`
      : `organizations/${levelId}/trafficAggregation/summary`;

    const unsubscribe = onSnapshot(
      doc(Firestore, docPath),
      (snapshot) => {
        try {
          if (snapshot.exists()) {
            setAggregatedData(snapshot.data());
          } else {
            setAggregatedData(null);
          }
          setLoading(false);
        } catch (err) {
          console.error('Error processing aggregated data:', err);
          setError('Failed to process traffic data');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error fetching aggregated data:', err);
        setError('Failed to fetch traffic data');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [levelId, level]);

  const chartData = useMemo(() => {
    if (!aggregatedData) return [];

    const organicMonthly = aggregatedData.organic?.monthlyData || {};
    const paidMonthly = aggregatedData.paid?.monthlyData || {};
    
    // Get all unique months
    const allMonths = new Set([
      ...Object.keys(organicMonthly),
      ...Object.keys(paidMonthly)
    ]);

    // Filter by time range
    const now = new Date();
    let monthsToShow = 6;
    switch (timeRange) {
      case '3months':
        monthsToShow = 3;
        break;
      case '12months':
        monthsToShow = 12;
        break;
      default:
        monthsToShow = 6;
    }

    const cutoffDate = new Date(now.getFullYear(), now.getMonth() - monthsToShow + 1, 1);
    
    const chartDataArray = Array.from(allMonths)
      .filter(month => {
        const monthDate = parseISO(`${month}-01`);
        return monthDate >= cutoffDate;
      })
      .sort()
      .map(month => {
        const organicData = organicMonthly[month] || { traffic: 0, cost: 0 };
        const paidData = paidMonthly[month] || { traffic: 0, cost: 0 };
        
        return {
          month,
          formattedMonth: format(parseISO(`${month}-01`), 'MMM yyyy'),
          organicTraffic: organicData.traffic || 0,
          paidTraffic: paidData.traffic || 0,
          organicCost: organicData.cost || 0,
          paidCost: paidData.cost || 0,
          totalTraffic: (organicData.traffic || 0) + (paidData.traffic || 0),
          totalCost: (organicData.cost || 0) + (paidData.cost || 0)
        };
      });

    return chartDataArray;
  }, [aggregatedData, timeRange]);

  const summaryStats = useMemo(() => {
    if (!aggregatedData) return null;

    const organic = aggregatedData.organic || {};
    const paid = aggregatedData.paid || {};

    return {
      totalOrganicTraffic: organic.totalTraffic || 0,
      totalPaidTraffic: paid.totalTraffic || 0,
      totalOrganicCost: organic.totalCost || 0,
      totalPaidCost: paid.totalCost || 0,
      totalTraffic: (organic.totalTraffic || 0) + (paid.totalTraffic || 0),
      totalCost: (organic.totalCost || 0) + (paid.totalCost || 0),
      sitesOrAccounts: level === 'account' ? aggregatedData.siteCount || 0 : aggregatedData.accountCount || 0
    };
  }, [aggregatedData, level]);

  const pieData = useMemo(() => {
    if (!summaryStats) return [];

    return [
      {
        name: 'Organic Traffic',
        value: summaryStats.totalOrganicTraffic,
        color: '#2e7d32'
      },
      {
        name: 'Paid Traffic', 
        value: summaryStats.totalPaidTraffic,
        color: '#ed6c02'
      }
    ].filter(item => item.value > 0);
  }, [summaryStats]);

  const formatTrafficValue = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  // const formatCurrency = (value) => {
  //   return new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: 'USD',
  //     minimumFractionDigits: 0,
  //     maximumFractionDigits: 0
  //   }).format(value);
  // };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!aggregatedData || !summaryStats) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No aggregated traffic data available yet. Data will appear once sites have been processed.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card sx={{ height: 300 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
            {title}
          </Typography>
          
          {/* Compact Summary */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h4" color="primary" fontWeight="bold" sx={{ fontSize: '1.75rem' }}>
              {formatTrafficValue(summaryStats.totalTraffic)}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Monthly Traffic
            </Typography>
            <Box display="flex" justifyContent="center" gap={1} mb={2}>
              <Chip label={`${formatTrafficValue(summaryStats.totalOrganicTraffic)} Organic`} color="success" size="small" />
              <Chip label={`${formatTrafficValue(summaryStats.totalPaidTraffic)} Paid`} color="warning" size="small" />
            </Box>
          </Box>

          {/* Compact Chart */}
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={chartData.slice(-3)}> {/* Show only last 3 months */}
              <XAxis 
                dataKey="formattedMonth" 
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide />
              <Tooltip 
                labelFormatter={(label) => `Month: ${label}`}
                formatter={(value, name) => [
                  formatTrafficValue(value),
                  name === 'organicTraffic' ? 'Organic' : 'Paid'
                ]}
                contentStyle={{ fontSize: '12px' }}
              />
              <Area 
                type="monotone" 
                dataKey="organicTraffic" 
                stackId="1"
                stroke="#2e7d32" 
                fill="#2e7d32"
                fillOpacity={0.7}
              />
              <Area 
                type="monotone" 
                dataKey="paidTraffic" 
                stackId="1"
                stroke="#ed6c02" 
                fill="#ed6c02"
                fillOpacity={0.7}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          
          {/* Summary Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {formatTrafficValue(summaryStats.totalTraffic)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Traffic
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {formatTrafficValue(summaryStats.totalOrganicTraffic)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Organic Traffic
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {formatTrafficValue(summaryStats.totalPaidTraffic)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Paid Traffic
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h4" color="text.primary" fontWeight="bold">
                  {summaryStats.sitesOrAccounts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {level === 'account' ? 'Sites' : 'Accounts'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Controls */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={(e, newViewType) => newViewType && setViewType(newViewType)}
                size="small"
                fullWidth
              >
                <ToggleButton value="trend">Trend</ToggleButton>
                <ToggleButton value="distribution">Distribution</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            
            {viewType === 'trend' && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={timeRange}
                    label="Time Range"
                    onChange={(e) => setTimeRange(e.target.value)}
                  >
                    <MenuItem value="3months">Last 3 Months</MenuItem>
                    <MenuItem value="6months">Last 6 Months</MenuItem>
                    <MenuItem value="12months">Last 12 Months</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip label="Organic" color="success" size="small" />
                <Chip label="Paid" color="warning" size="small" />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Chart */}
        <ResponsiveContainer width="100%" height={400}>
          {viewType === 'trend' ? (
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="formattedMonth" 
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickFormatter={formatTrafficValue}
              />
              <Tooltip 
                labelFormatter={(label) => `Month: ${label}`}
                formatter={(value, name) => [
                  formatTrafficValue(value),
                  name === 'organicTraffic' ? 'Organic Traffic' :
                  name === 'paidTraffic' ? 'Paid Traffic' : name
                ]}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="organicTraffic" 
                stackId="1"
                stroke="#2e7d32" 
                fill="#2e7d32"
                fillOpacity={0.7}
                name="Organic Traffic"
              />
              <Area 
                type="monotone" 
                dataKey="paidTraffic" 
                stackId="1"
                stroke="#ed6c02" 
                fill="#ed6c02"
                fillOpacity={0.7}
                name="Paid Traffic"
              />
            </AreaChart>
          ) : (
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatTrafficValue(value)} />
            </PieChart>
          )}
        </ResponsiveContainer>

        {aggregatedData.lastUpdated && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Last updated: {aggregatedData.lastUpdated.toDate?.()?.toLocaleDateString() || 'Unknown'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default AggregatedTrafficChart;